import { CHAT_STEP } from "./chatStates";

/*
 * What typed text may do at each step of the chat flow.
 *
 * model:  the server may ask the model to read the text. Only where the
 *         customer is choosing what to do next; never once a transfer is
 *         being built. The backend mirrors this set as MODEL_STEPS in
 *         app/services/chat_service.py.
 * text:   which deterministic handler reads the text. "cancel_only" means a
 *         card owns the step and typing can only cancel it.
 * card:   the widget that takes the input at this step.
 * secret: a PIN is being collected; the backend refuses bare digits typed
 *         here (PIN_STEPS in chat_service.py).
 * hint:   the reply when text is typed at a "cancel_only" step.
 */
export const INPUT_TEXT = Object.freeze({
  INTENT: "intent",
  RECIPIENT: "recipient",
  AMOUNT: "amount",
  FUNDING_METHOD: "funding_method",
  REVIEW_CHOICE: "review_choice",
  CANCEL_ONLY: "cancel_only",
});

const AFTER_TRANSFER = Object.freeze({
  model: true,
  text: INPUT_TEXT.INTENT,
  secret: false,
});

const PIN_STEP = Object.freeze({
  model: false,
  text: INPUT_TEXT.CANCEL_ONLY,
  card: "secure_pin",
  secret: true,
  hint: "chat.transfer.use_secure_controls",
});

export const INPUT_POLICY = Object.freeze({
  [CHAT_STEP.IDLE]: { model: true, text: INPUT_TEXT.INTENT, card: null, secret: false },
  [CHAT_STEP.CHOOSING_RECIPIENT]: {
    model: false,
    text: INPUT_TEXT.RECIPIENT,
    card: "recipient_picker",
    secret: false,
  },
  [CHAT_STEP.CREATING_RECIPIENT]: {
    model: false,
    text: INPUT_TEXT.CANCEL_ONLY,
    card: "recipient_form",
    secret: false,
    hint: "chat.recipient.form_pending",
  },
  [CHAT_STEP.ENTERING_AMOUNT]: { model: false, text: INPUT_TEXT.AMOUNT, card: null, secret: false },
  [CHAT_STEP.GETTING_QUOTE]: {
    model: false,
    text: INPUT_TEXT.CANCEL_ONLY,
    card: null,
    secret: false,
    hint: "chat.quote.wait",
  },
  [CHAT_STEP.CHOOSING_FUNDING]: {
    model: false,
    text: INPUT_TEXT.FUNDING_METHOD,
    card: "funding_replies",
    secret: false,
  },
  [CHAT_STEP.REVIEWING_DRAFT]: {
    model: false,
    text: INPUT_TEXT.REVIEW_CHOICE,
    card: "transfer_review",
    secret: false,
  },
  [CHAT_STEP.CREATING_TRANSFER]: PIN_STEP,
  [CHAT_STEP.AWAITING_PIN]: PIN_STEP,
  [CHAT_STEP.CONFIRMING_TRANSFER]: PIN_STEP,
  [CHAT_STEP.AWAITING_BANK_FUNDING]: { ...AFTER_TRANSFER, card: "bank_funding" },
  [CHAT_STEP.AWAITING_CARD_FUNDING]: { ...AFTER_TRANSFER, card: "card_funding" },
  [CHAT_STEP.TRACKING_TRANSFER]: { ...AFTER_TRANSFER, card: "transfer_status" },
});

export function inputPolicy(step) {
  return INPUT_POLICY[step] || INPUT_POLICY[CHAT_STEP.IDLE];
}
